import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle2, MailCheck, XCircle, Loader2, Sparkles } from "lucide-react";

const MAX_POLLS = 8;
const POLL_MS = 2000;

export default function DonationSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [amount, setAmount] = useState(null);
  const [note, setNote] = useState(null);
  const [noteLoading, setNoteLoading] = useState(false);
  const [receiptSent, setReceiptSent] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let attempts = 0;
    let cancelled = false;

    async function poll() {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/donations/status/${sessionId}`);
        setAmount(data.amount);
        if (data.receipt_sent) setReceiptSent(true);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (attempts >= MAX_POLLS) {
          setStatus("pending");
          return;
        }
        attempts += 1;
        setTimeout(poll, POLL_MS);
      } catch {
        setStatus("failed");
      }
    }
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  useEffect(() => {
    if (status !== "paid" || !sessionId || note) return;
    setNoteLoading(true);
    api
      .post("/ai/thank-you", { session_id: sessionId })
      .then((r) => setNote(r.data.note))
      .catch(() => {})
      .finally(() => setNoteLoading(false));
  }, [status, sessionId, note]);

  // The receipt email fires asynchronously right after payment is confirmed —
  // poll a few times so we can show the "receipt emailed" line.
  useEffect(() => {
    if (status !== "paid" || receiptSent || !sessionId) return;
    let attempts = 0;
    let cancelled = false;
    const check = () => {
      if (cancelled) return;
      api
        .get(`/donations/status/${sessionId}`)
        .then((r) => {
          if (r.data.receipt_sent) {
            setReceiptSent(true);
          } else if (++attempts < 4) {
            setTimeout(check, 2000);
          }
        })
        .catch(() => {});
    };
    const t = setTimeout(check, 2000);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [status, receiptSent, sessionId]);

  return (
    <section className="py-24 min-h-[60vh]">
      <div className="max-w-[720px] mx-auto px-6 text-center" data-testid="donation-success-page">
        {status === "checking" && (
          <>
            <Loader2 className="animate-spin mx-auto text-palm" size={48} />
            <h1 className="font-display text-4xl mt-6 mb-3">Confirming your gift…</h1>
            <p className="text-ink/70">Hold tight — we're checking with Stripe.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="mx-auto text-palm" size={56} />
            <h1 className="font-display text-4xl mt-6 mb-3">Thank you.</h1>
            <p className="text-ink/80 text-lg mb-2">Your gift of <strong>${amount?.toFixed(2)}</strong> is on its way to HOFA's programs in Liberia.</p>

            <div className="mt-8 max-w-[520px] mx-auto bg-white border border-ink/10 rounded-[14px] p-6 text-left" data-testid="donation-thank-you-card">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.14em] font-bold text-palm mb-2">
                <Sparkles size={14} /> A note from Mango Town
              </div>
              {noteLoading && !note && (
                <div className="flex items-center gap-2 text-ink/50 text-sm">
                  <Loader2 className="animate-spin" size={14} /> Writing a personal thank-you…
                </div>
              )}
              {note && (
                <p className="text-ink/85 text-[16px] leading-[1.65] whitespace-pre-wrap" data-testid="donation-thank-you-note">{note}</p>
              )}
            </div>

            {receiptSent && (
              <p className="mt-4 inline-flex items-center gap-2 text-sm text-palm font-semibold" data-testid="donation-receipt-sent">
                <MailCheck size={16} /> A receipt has been emailed to you.
              </p>
            )}

            <Link to="/" className="inline-block mt-10 bg-palm text-white font-bold rounded-full px-7 py-3.5 no-underline hover:-translate-y-0.5 hover:shadow-xl transition" data-testid="donation-success-home-btn">Back to home</Link>
          </>
        )}
        {status === "pending" && (
          <>
            <Loader2 className="animate-spin mx-auto text-mango" size={48} />
            <h1 className="font-display text-4xl mt-6 mb-3">Still processing…</h1>
            <p className="text-ink/70">Stripe hasn't confirmed the payment yet. If it goes through, we'll email you a receipt.</p>
            <Link to="/get-involved" className="mt-6 inline-block border-2 border-palm text-palm font-bold rounded-full px-6 py-3 no-underline hover:bg-palm hover:text-white transition">Back to donate</Link>
          </>
        )}
        {(status === "failed" || status === "expired") && (
          <>
            <XCircle className="mx-auto text-berry" size={56} />
            <h1 className="font-display text-4xl mt-6 mb-3">Something went wrong.</h1>
            <p className="text-ink/70 mb-6">We couldn't confirm your donation. If you were charged, please contact HOFA and we'll sort it out.</p>
            <Link to="/get-involved" className="inline-block bg-palm text-white font-bold rounded-full px-6 py-3 no-underline">Try again</Link>
          </>
        )}
      </div>
    </section>
  );
}
